import { get } from "lodash";

import { INVALID_INPUT_HEADER } from "../constants";

/**
 * Handles errors that occur during alternative link processing
 */
export const handleProcessAlternativeLinkError = ({
  error,
  linkAttempts,
  setLinkAttempts,
  setError,
  nextStep,
  setPromiseInProgress,
  setLinkError,
  carrier,
}: {
  error: any;
  linkAttempts: Record<string, number>;
  setLinkAttempts: (attempts: Record<string, number>) => void;
  setError: (errorMessage: string) => void;
  nextStep: (nextStep: string, ...args: any[]) => void;
  setPromiseInProgress: (inProgress: boolean) => void;
  setLinkError: (errorType: string) => void;
  carrier: string;
}) => {
  console.error(
    "Processing alternative link failed with error: ",
    JSON.stringify(error)
  );

  switch (error.code) {
    case 400: {
      switch (error.message) {
        case "Your lookup is invalid or the policy cannot be found. Verify you have the correct details and try again.": {
          // If user has already tried 2 times
          if (get(linkAttempts, carrier, 0) + 1 > 2) {
            nextStep("max-link-attempts");
            return;
          }

          // Otherwise, increment the link attempt counter
          setLinkAttempts({
            ...linkAttempts,
            [carrier]: get(linkAttempts, carrier, 0) + 1,
          });

          setPromiseInProgress(false);
          setError(error.message);
          return;
        }
        case "Policy is not active.": {
          setPromiseInProgress(false);
          nextStep("policy-not-active");
          return;
        }
        case "This policy type is not supported by the client.": {
          setPromiseInProgress(false);
          setError(error.message);
          return;
        }
        case "Sandbox only supports test inputs. Please try again.":
          setPromiseInProgress(false);
          setError(error.message);
          return;

        case "Sorry, we didn't find any compatible policies. Please try again using another verification method.":
          setPromiseInProgress(false);
          setError(error.message);
          return;
        case "Incomplete policy data available. Please try again using another verification method.":
          setPromiseInProgress(false);
          setError(error.message);
          return;
        case "Sorry, we are unable to access this policy because there is no lienholder present or the lienholder provided is not listed on this policy. Please try again with another verification method.":
          setPromiseInProgress(false);
          setError(error.message);
          return;
        default:
          console.log("Unknown 400 error from alternative link: ", error);
          setLinkError("look up your policy");
          nextStep("carrier-error");
          return;
      }
    }
    case 429: {
      // Concurrency-limit: the backend returns HTTP 429 for this condition.
      setPromiseInProgress(false);
      setError(error.message ?? "Sorry, we are experiencing high demand for this insurance carrier. Please try again in a few minutes.");
      return;
    }

    case 500: {
      switch (error.message) {
        case "Oops! Something went wrong.":
        case "Oops something went wrong. Please try again later.":
          console.log("500 error from alternative link: ", error);
          setLinkError("look up your policy");
          nextStep("carrier-error");
          return;
        default:
          console.error("Unknown 500 error from alternative link: ", error);
          setLinkError("look up your policy");
          nextStep("carrier-error");
          return;
      }
    }

    default:
      console.error("Unknown error from alternative link: ", error);
      setLinkError("look up your policy");
      nextStep("carrier-error");
      return;
  }
};

/**
 * Handles errors that occur during stub account operations
 */
export const handleStubAccountError = (
  stubAccountError: any,
  nextStep: (nextStep: any, ...args: any[]) => void,
  setError: any
) => {
  if (stubAccountError) {
    switch (get(stubAccountError, "code")) {
      case 500:
        console.log(JSON.stringify(stubAccountError));
        nextStep("axle-error");
        setError(stubAccountError.message);
        return;
      default:
        nextStep("connection-error");
        return;
    }
  }
};

/**
 * Handles the errors that occur during stub policy operations
 * @param stubPolicyResponseError
 * @param nextStep
 * @param setError
 * @returns
 */
export const handleStubPolicyError = (
  stubPolicyResponseError: any,
  nextStep: (nextStep: any, ...args: any[]) => void,
  setError: any
) => {
  switch (get(stubPolicyResponseError, "code")) {
    case 400:
      if (
        stubPolicyResponseError.message ===
        "Input validation failed due to one or more missing or invalid parameters. Review the request and try again."
      ) {
        setError(INVALID_INPUT_HEADER);
        return;
      }
    // Allow the fallthrough on purpose? This is what we had previously in the code
    case 500:
      console.log(JSON.stringify(stubPolicyResponseError));
      console.log(`Failed to stub policy`);
      nextStep("failed");
      setError(stubPolicyResponseError.message);
      return;

    default:
      nextStep("connection-error");
      return;
  }
};
